import type { ClientBrandSummaryDto } from '@/apis/backend/generated/models/clientBrandSummaryDto';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  useClientSummariesControllerGetBrandSummaries,
} from '@/apis/backend/generated/client-summaries/client-summaries';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha, styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/settings/client/brand/profiles')({
  component: BrandProfilesList,
});

function BrandProfilesList() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { activeClient, switchClient } = useOrganization();
  const { orgId } = Route.useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: brandSummaries, isLoading } =
    useClientSummariesControllerGetBrandSummaries(orgId, {
      query: { enabled: !!orgId },
    });

  const filtered = useMemo(() => {
    const items = brandSummaries?.items ?? [];
    const sorted = [...items].sort((a, b) => a.clientName.localeCompare(b.clientName));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase().trim();
    return sorted.filter((item) => item.clientName.toLowerCase().includes(q));
  }, [brandSummaries?.items, searchQuery]);

  const handleOpenDetail = (clientId: string) => {
    if (clientId !== activeClient?.id) {
      switchClient(clientId);
    }
    navigate({ to: paths.dashboard.orgSettings(orgId).client.brandDetail(clientId) });
  };

  return (
    <>
      <SearchField value={searchQuery} onChange={setSearchQuery} count={filtered.length} />
      {isLoading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={88} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((item) => (
            <BrandSummaryCard
              key={item.clientId}
              item={item}
              isActive={item.clientId === activeClient?.id}
              onClick={() => handleOpenDetail(item.clientId)}
            />
          ))}
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {searchQuery
                ? t('brandProfiles.empty.noMatch', 'No clients match your search')
                : t('brandProfiles.empty.noClients', 'No clients found')}
            </Typography>
          )}
        </Stack>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function SearchField({
  value,
  onChange,
  count,
}: {
  value: string;
  onChange: (v: string) => void;
  count: number;
}) {
  const { t } = useTranslation('settings');
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <TextField
        autoFocus
        size="small"
        placeholder={t('brandProfiles.search.placeholder', 'Search clients...')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          width: 260,
          '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' },
        }}
      />
      <Typography variant="caption" color="text.disabled">
        {t('brandProfiles.search.count', '{{count}} {{label}}', {
          count,
          label: count === 1
            ? t('brandProfiles.search.clientSingular', 'client')
            : t('brandProfiles.search.clientPlural', 'clients'),
        })}
      </Typography>
    </Stack>
  );
}

function BrandSummaryCard({
  item,
  isActive,
  onClick,
}: {
  item: ClientBrandSummaryDto;
  isActive: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('settings');
  const { completeness } = item;
  const missing = [
    !completeness.hasLogo && t('brandProfiles.missing.logo', 'Logo'),
    !completeness.hasPaletteColors && t('brandProfiles.missing.paletteColors', 'Brand colors'),
    !completeness.hasButtonColors && t('brandProfiles.missing.buttonColors', 'Button colors'),
    !completeness.hasVoice && t('brandProfiles.missing.voice', 'Voice'),
    !completeness.hasDescription && t('brandProfiles.missing.description', 'Description'),
    !completeness.hasIndustry && t('brandProfiles.missing.industry', 'Industry'),
    !completeness.hasSocialLinks && t('brandProfiles.missing.socialLinks', 'Social Links'),
    !completeness.hasSender && t('brandProfiles.missing.sender', 'Sender details'),
  ].filter(Boolean) as string[];

  return (
    <ClientCard onClick={onClick}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        {item.logoUrl ? (
          <Avatar
            src={item.logoUrl}
            variant="rounded"
            sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              bgcolor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'grey.100',
              color: isActive ? 'primary.main' : 'text.secondary',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {item.clientName.charAt(0).toUpperCase()}
          </Avatar>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {item.clientName}
            </Typography>
            {isActive && (
              <Chip label={t('brandProfiles.card.active', 'Active')} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} color="primary" />
            )}
          </Stack>

          {item.hasBrandProfile ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={completeness.score}
                sx={{
                  width: 80,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    bgcolor:
                      completeness.score === 100
                        ? 'success.main'
                        : completeness.score >= 60
                          ? 'warning.main'
                          : 'error.main',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('brandProfiles.card.scoreComplete', '{{score}}% complete', { score: completeness.score })}
              </Typography>
              {missing.length > 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t('brandProfiles.card.missing', 'Missing: {{items}}', { items: missing.join(', ') })}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
              {t('brandProfiles.card.noProfile', 'No brand profile set up')}
            </Typography>
          )}
        </Box>
      </Stack>

      <Iconify icon="eva:chevron-right-fill" width={20} sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </ClientCard>
  );
}

// ----------------------------------------------------------------------

const ClientCard = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(2, 2.5),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  textAlign: 'left',
  gap: theme.spacing(1),
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));
